
import { useEffect, useMemo, useState } from 'react';
import { APP_NAME, APP_ORG_NAME, APP_ORG_NAME_SHORT, APP_ORG_URL } from '../../config/brandingConfig';

const POLITE_MESSAGES = [
  `Welcome, we are preparing your ${APP_ORG_NAME_SHORT} workspace.`,
  'Loading the tools that support your daily work.',
  'Organizing your modules for a smoother experience.',
  'Preparing secure access to your assigned services.',
  'Setting up your workspace with care.',
  'Almost ready, connecting the modules you need.',
  'Thank you for your patience as we prepare the platform.',
  'Your workspace is being optimized for today’s tasks.',
  'Bringing together your dashboards, modules, and permissions.',
  `Preparing a reliable experience for ${APP_ORG_NAME}.`,
];

const PHASE_LABELS = {
  core: 'Securing core services',
  modules: 'Preparing application modules',
};

const PHASE_HELP_TEXT = {
  core: 'Checking authentication, permissions, and platform services.',
  modules: 'Loading the modules assigned to your role.',
};

function formatPercent(done, total) {
  if (!total) return 0;
  return Math.min(100, Math.round((done / total) * 100));
}

function LoadingDots() {
  return (
    <div className="flex items-center justify-center gap-2" aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="h-2.5 w-2.5 rounded-full bg-primary animate-bootstrap-dot motion-reduce:animate-none"
          style={{ animationDelay: `${index * 0.18}s` }}
        />
      ))}
    </div>
  );
}

function LogoMark() {
  const logoSrc = `${process.env.PUBLIC_URL || ''}/logo.svg`;

  return (
    <div className="relative mx-auto mb-8 flex h-28 w-28 items-center justify-center">
      <div
        className="absolute inset-0 rounded-full animate-bootstrap-spin motion-reduce:animate-none"
        style={{
          background:
            'conic-gradient(from 0deg, var(--color-primary) 0deg 100deg, transparent 100deg 180deg, var(--color-secondary) 180deg 280deg, transparent 280deg 360deg)',
          mask: 'radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1px))',
          WebkitMask:
            'radial-gradient(farthest-side, transparent calc(100% - 1.5px), #000 calc(100% - 1px))',
        }}
        aria-hidden="true"
      />

      <div className="relative flex h-[4.7rem] w-[4.7rem] items-center justify-center rounded-full border border-white/80 bg-white/95 shadow-[0_24px_60px_-22px_rgba(20,57,104,0.5)] backdrop-blur-sm">
        <img src={logoSrc} alt={APP_ORG_NAME_SHORT} className="h-11 w-auto" />
      </div>
    </div>
  );
}

function ProgressSteps({ phase, percent }) {
  const steps = [
    { key: 'core', label: 'Core' },
    { key: 'modules', label: 'Modules' },
    { key: 'ready', label: 'Ready' },
  ];

  const activeIndex = phase === 'core' ? 0 : percent >= 100 ? 2 : 1;

  return (
    <div className="mt-6 grid grid-cols-3 gap-2" aria-hidden="true">
      {steps.map((step, index) => {
        const isActive = index === activeIndex;
        const isDone = index < activeIndex;

        return (
          <div
            key={step.key}
            className={[
              'rounded-full border px-3 py-1.5 text-center text-[0.68rem] font-semibold uppercase tracking-wide transition-all duration-300',
              isActive
                ? 'border-primary/30 bg-primary/10 text-primary shadow-sm'
                : isDone
                  ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                  : 'border-slate-200 bg-white/70 text-text-muted',
            ].join(' ')}
          >
            {step.label}
          </div>
        );
      })}
    </div>
  );
}

export default function BootstrapShell({ progress }) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [messageCycle, setMessageCycle] = useState(0);

  const done = progress?.done ?? 0;
  const total = progress?.total ?? 0;
  const failed = progress?.failed ?? 0;
  const percent = formatPercent(done, total);
  const phase = progress?.phase || 'core';
  const currentLabel = progress?.label;

  const phaseLabel = PHASE_LABELS[phase] || PHASE_LABELS.modules;
  const phaseHelpText = PHASE_HELP_TEXT[phase] || PHASE_HELP_TEXT.modules;

  const politeMessage = useMemo(
    () => POLITE_MESSAGES[messageIndex % POLITE_MESSAGES.length],
    [messageIndex]
  );

  useEffect(() => {
    const timer = window.setInterval(() => {
      setMessageIndex((current) => (current + 1) % POLITE_MESSAGES.length);
      setMessageCycle((current) => current + 1);
    }, 3600);

    return () => window.clearInterval(timer);
  }, []);

  const statusText =
    total > 0 ? `${done} of ${total} components ready` : 'Starting platform services…';

  return (
    <div className="relative flex min-h-screen flex-col overflow-hidden bg-surface-muted text-text-primary">
      <div
        className="relative z-10 mx-auto flex w-full max-w-xl flex-1 flex-col items-center justify-center px-5 py-10 sm:px-8"
        role="status"
        aria-live="polite"
        aria-busy="true"
      >
        <div className="relative w-full overflow-hidden animate-bootstrap-rise rounded-[1.85rem] border border-white/70 p-[1px] shadow-card motion-reduce:animate-none">
          <div
            className="absolute inset-0 rounded-[1.85rem] bg-gradient-to-br from-primary/35 via-accent-blue/20 to-accent-orange/30"
            aria-hidden="true"
          />
          <div className="relative overflow-hidden rounded-[calc(1.85rem-1px)] backdrop-blur-xl">
            <div
              className="absolute inset-0 bg-auth-pattern opacity-[0.14]"
              aria-hidden="true"
            />
            <div
              className="absolute inset-0 bg-[linear-gradient(145deg,rgba(255,255,255,0.97)_0%,rgba(255,255,255,0.9)_42%,color-mix(in_srgb,var(--color-primary)_8%,white)_100%)]"
              aria-hidden="true"
            />
            <div
              className="absolute -right-16 -top-16 h-48 w-48 rounded-full bg-primary/10 blur-3xl animate-bootstrap-glow motion-reduce:animate-none"
              aria-hidden="true"
            />
            <div
              className="absolute -bottom-20 -left-12 h-56 w-56 rounded-full bg-accent-orange/10 blur-3xl animate-bootstrap-drift motion-reduce:animate-none"
              style={{ animationDelay: '1.5s' }}
              aria-hidden="true"
            />

            <div className="relative p-8 sm:p-10">
          <LogoMark />

          <div className="text-center">
            <p className="text-[1rem] font-semibold uppercase tracking-[0.24em] text-primary">
              {APP_NAME}
            </p>

            <h1 className="mt-2 bg-gradient-to-r from-slate-900 via-primary to-slate-800 bg-clip-text text-2xl font-semibold tracking-tight text-transparent sm:text-3xl">
              Preparing your workspace
            </h1>
          </div>

          <p
            key={messageCycle}
            className="mx-auto mt-5 min-h-[3.25rem] max-w-md text-center text-sm leading-relaxed text-text-secondary animate-bootstrap-message motion-reduce:animate-none"
          >
            {politeMessage}
          </p>

          <div className="mt-8">
            <div className="mb-3 flex items-end justify-between gap-4">
              <div>
                <p className="text-[10px] font-medium uppercase tracking-wide text-text-muted">
                  {phaseLabel}
                </p>

                <p className="mt-0.5 text-xs font-semibold text-text-primary">
                  {statusText}
                </p>

                <p className="mt-0.5 max-w-xs text-[10px] leading-snug text-text-muted">
                  {phaseHelpText}
                </p>
              </div>

              <p className="text-2xl font-semibold tabular-nums text-primary sm:text-3xl">
                {percent}
                <span className="text-sm font-medium text-text-muted">%</span>
              </p>
            </div>

            <div className="relative h-3 overflow-hidden rounded-full bg-slate-100/90 ring-1 ring-inset ring-slate-200/70">
              <div
                className="relative h-full rounded-full transition-[width] duration-700 ease-out"
                style={{
                  width: `${Math.max(percent, 5)}%`,
                  background:
                    'linear-gradient(to right, var(--color-primary), var(--color-secondary))',
                }}
              >
                <div
                  className="absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/60 to-transparent animate-bootstrap-shimmer motion-reduce:hidden"
                  aria-hidden="true"
                />
              </div>
            </div>

            <ProgressSteps phase={phase} percent={percent} />

            {currentLabel ? (
              <div className="mt-5 flex items-center justify-center">
                <span className="inline-flex max-w-full items-center gap-2 rounded-full border border-primary/15 bg-primary/5 px-3.5 py-1.5 text-xs text-text-secondary">
                  <span className="relative flex h-2 w-2 shrink-0">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary/50 opacity-70 motion-reduce:animate-none" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                  </span>

                  <span className="truncate">
                    Preparing{' '}
                    <span className="font-semibold text-text-primary">
                      {currentLabel}
                    </span>
                  </span>
                </span>
              </div>
            ) : null}

            {failed > 0 ? (
              <p className="mt-4 rounded-xl border border-amber-200 bg-amber-50 px-4 py-2 text-center text-xs font-medium text-amber-800">
                {failed} module{failed === 1 ? '' : 's'} will continue loading when opened.
              </p>
            ) : null}
          </div>

          <div className="mt-8">
            <LoadingDots />
          </div>
            </div>
          </div>
        </div>
      </div>

      <footer className="relative z-10 pb-8 text-center text-xs font-medium tracking-wide text-text-muted">
        <span>{APP_ORG_NAME}</span>
        <span className="mx-2 text-slate-300">•</span>
        <a
          href={APP_ORG_URL}
          target="_blank"
          rel="noreferrer"
          className="text-primary hover:underline"
        >
          humanrights.or.tz
        </a>
      </footer>
    </div>
  );
}

